import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { SectionHead } from "@/components/SectionHead";
import { Mockup } from "@/components/Mockup";
import { CtaStrip } from "@/components/CtaStrip";

const FEATURES = [
  {
    title: "White-Label Dashboards",
    body: "Fully branded election-results experience on your domain. Your logo, your colours, your editorial identity. No SevenM branding visible to your audience.",
    label: "[Product Screenshot: Branded White-Label Dashboard]",
    src: "/screenshots/whitelabel.jpg",
    position: "top",
    testid: "media-feature-white-label",
  },
  {
    title: "Embeddable Widgets",
    body: "Drop election result modules into any page on your site. Seat tally bars, constituency maps, party standings, candidate cards — all via simple iframe or script embed. Works with WordPress, custom CMS, or any web platform.",
    label: "[Product Screenshot: Embeddable Widget Example]",
    src: "/screenshots/live-dashboard.jpg",
    testid: "media-feature-widgets",
  },
  {
    title: "Live Election Results Day",
    body: "Real-time seat tallies, constituency-level updates, majority tracker and interactive maps — all auto-refreshing. Built for the traffic spikes that election results days bring.",
    label: "[Product Screenshot: Live Results Dashboard]",
    src: "/screenshots/dashboard-live.jpg",
    testid: "media-feature-live-night",
  },
  {
    title: "Analytical Tools",
    body: "Swing analysis, vote-share comparisons, margin breakdowns, constituency turnover maps and historical context — the analytical layer that turns your coverage from results-reading into insight.",
    label: "[Product Screenshot: Analytical Tools View]",
    src: "/screenshots/vote-share.jpg",
    testid: "media-feature-analytics",
  },
  {
    title: "Admin Control Panel",
    body: "Manage featured elections, control state and constituency visibility, and curate what your audience sees — without touching code.",
    label: "[Product Screenshot: Admin Control Panel]",
    src: "/screenshots/admin-panel.jpg",
    testid: "media-feature-admin",
  },
  {
    title: "Monetisation Built In",
    body: "Google AdSense, direct ad networks, and custom ad placement — your election coverage generates revenue, not just traffic.",
    label: "[Product Screenshot: Ad Placement Manager]",
    src: "/screenshots/ads.jpg",
    testid: "media-feature-monetisation",
  },
];

const TECH = [
  "CDN-backed delivery for election-results-day traffic",
  "Mobile-responsive, PWA-compatible",
  "Multi-language support (English, Hindi, regional)",
  "Role-based access controls",
  "Encrypted data pipelines and secure APIs",
  "Google Analytics and Tag Manager integration",
];

const ForMedia = () => (
  <div data-testid="for-media-page">
    <Seo
      title="For Media — White-Label Election Coverage Infrastructure | Anlytx for Elections"
      description="White-label election dashboards, embeddable widgets and live-result modules for Indian media organisations — under your own brand."
    />

    <section className="relative overflow-hidden bg-[#0b1322]" data-testid="media-hero">
      <div className="absolute inset-0 grid-faint-dark opacity-70" />
      <div className="absolute -top-32 left-1/3 h-80 w-[60%] rounded-full bg-[#b91c1c]/15 blur-3xl" />
      <div className="relative mx-auto max-w-4xl px-6 py-20 text-center sm:py-28">
        <Reveal>
          <p className="font-mono-data text-xs font-medium uppercase tracking-[0.24em] text-red-400">
            For Media Organisations
          </p>
          <h1 className="mt-4 font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl text-balance">
            Election Coverage Infrastructure for Indian Media
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-300 md:text-lg">
            Give your audience the election experience they expect — live results, interactive maps, constituency data
            and analytical depth — under your own brand. Delivered as a full white-label dashboard, easy-to-integrate
            widgets, or data-only API access.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="border-b border-slate-200 bg-white" data-testid="delivery-modes-band">
      <div className="mx-auto max-w-7xl px-6 py-14">
        <Reveal>
          <p className="text-center font-mono-data text-xs font-medium uppercase tracking-[0.24em] text-[#b91c1c]">
            Delivered Three Ways
          </p>
        </Reveal>
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {[
            {
              num: "01",
              title: "Full White-Label Dashboard",
              body: "A complete branded results experience on your domain. Nothing to build.",
              target: "media-feature-white-label",
              testid: "mode-band-dashboard",
            },
            {
              num: "02",
              title: "Easy-to-Integrate Widgets",
              body: "Live result modules embedded in your own website or app via iframe or script.",
              target: "media-feature-widgets",
              testid: "mode-band-widgets",
            },
            {
              num: "03",
              title: "API Mode",
              body: "Data-only access for teams building their own experience on their own stack.",
              target: "media-feature-live-night",
              testid: "mode-band-api",
            },
          ].map((m, i) => (
            <Reveal key={m.testid} delay={i * 0.1}>
              <button
                type="button"
                onClick={() => {
                  const el = document.getElementById(m.target);
                  if (el) {
                    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -88 });
                    else el.scrollIntoView({ behavior: "smooth" });
                  }
                }}
                data-testid={m.testid}
                className="group h-full w-full rounded-xl border border-slate-200 bg-[#f8fafc] p-6 text-left transition-colors duration-300 hover:border-[#b91c1c]/50"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono-data text-xs font-semibold tracking-[0.2em] text-[#b91c1c]">{m.num}</span>
                  <ArrowRight className="h-4 w-4 text-slate-300 transition-all duration-300 group-hover:translate-x-1 group-hover:text-[#b91c1c]" />
                </div>
                <h2 className="mt-3 font-heading text-lg font-semibold text-slate-900">{m.title}</h2>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{m.body}</p>
              </button>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-white" data-testid="media-features">
      <div className="mx-auto max-w-7xl px-6 py-20 sm:py-24">
        <SectionHead
          eyebrow="What You Get"
          title="A Complete Election-Results-Day Stack, Branded as Yours"
        />
        <div className="mt-16 space-y-20">
          {FEATURES.map((f, i) => (
            <div
              key={f.testid}
              id={f.testid}
              className={`grid items-center gap-10 lg:grid-cols-2 ${i % 2 === 1 ? "" : ""}`}
              data-testid={f.testid}
            >
              <Reveal className={i % 2 === 1 ? "lg:order-2" : ""}>
                <p className="font-mono-data text-xs font-semibold tracking-[0.2em] text-[#b91c1c]">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="mt-3 font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                  {f.title}
                </h3>
                <p className="mt-4 text-base leading-relaxed text-slate-600">{f.body}</p>
              </Reveal>
              <Reveal delay={0.12} className={i % 2 === 1 ? "lg:order-1" : ""}>
                <Mockup label={f.label} src={f.src} position={f.position} />
              </Reveal>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="relative overflow-hidden bg-[#f8fafc]" data-testid="media-tech-confidence">
      <div className="absolute inset-0 dot-grid opacity-30" />
      <div className="relative mx-auto max-w-7xl px-6 py-20 sm:py-24">
        <SectionHead
          eyebrow="Technical Confidence"
          title="Engineered for Counting-Day Traffic"
          sub="The infrastructure assumptions behind every deployment."
        />
        <div className="mx-auto mt-12 grid max-w-4xl gap-4 sm:grid-cols-2">
          {TECH.map((t, i) => (
            <Reveal key={t} delay={(i % 2) * 0.08}>
              <div
                className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-5"
                data-testid={`tech-item-${i + 1}`}
              >
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#fef2f2]">
                  <Check className="h-3 w-3 text-[#b91c1c]" />
                </span>
                <p className="text-sm font-medium leading-relaxed text-slate-700">{t}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1} className="mt-10 text-center">
          <p className="text-sm text-slate-500">
            Media partnerships:{" "}
            <a
              href="mailto:hello@electionanlytx.com"
              data-testid="media-email-link"
              className="font-semibold text-[#b91c1c] hover:text-[#991b1b]"
            >
              hello@electionanlytx.com
            </a>
          </p>
        </Reveal>
      </div>
    </section>

    <CtaStrip
      testid="media-cta-strip"
      heading="Put Your Brand on the Results Your Audience Is Refreshing"
      subheading="Walk through a live deployment with our team, or start with an embeddable widget pilot."
      primary={{ label: "Request Media Access", to: "/contact?type=media" }}
      secondary={{ label: "Schedule a Walkthrough", to: "/contact?type=media" }}
    />
  </div>
);

export default ForMedia;
